import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom';
import _ from 'lodash'

import { ListTile, Animation } from 'components'
import { PageInput, PageBlock, SortableForest } from 'features'
import { AnimatePresence } from 'framer-motion'
import { SideSection, ProfileSection, UpgradeSection } from './components'
import { FaHome, FaInbox, FaCalendarDay, FaBolt, FaCube, FaCog } from 'react-icons/fa'

import * as actionCreators from 'store/actions'
import { pageContext } from 'context'

const PopContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    top: 0.3rem;
    left: 0;
    float: left;
    background-color: var(--secondary-color);
    z-index: 1299;
    width: 15rem;
    min-height: 30vh;
    max-height: 80vh;
    border-radius: 0 5px 5px 0;
    box-shadow: var(--box-shadow);
    overflow-y: auto;
`

const Wrapper = styled.div`
    height: 100%;
    margin: 0;
    overflow-wrap: break-word;
    background-color: var(--secondary-color);
    z-index:1;
    width: 15rem;
    min-width: 15rem;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const Container = styled.div`
    height: ${props => `calc(${props.height}px - 2.5rem)` || 'auto'};
    /* height: 20rem; */
    width: 15rem;
    position: fixed;
    /* top: 2.5rem; */
    overflow: clip;
    display: flex;
    flex-direction: column;
`

const PageBlocks = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto; /* Need this to prevent PageContainer from causing this to overgrow*/
    padding: 0.5rem;
`
const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    /* border: 1px solid red; */
    /* overflow-y: ${props => props.disableScroll ? 'hidden' : 'auto'}; */
    padding: ${props => props.padding};
    /* flex-shrink: 3; */
    /* flex: 1 1 min-content; */
`

const Placeholder = styled.div`
    margin-left: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    color: var(--secondary-accent-color);
`

const defaultStyle = {
    // fontWeight: 'bolder',
    fontSize: '0.9rem',
    lineHeight: '0.9rem',
    height: '2rem'
}

const selectedStyle = {
    // fontWeight: 'bolder',
    fontSize: '0.9rem',
    lineHeight: '0.9rem',
    height: '2rem',
    backgroundColor: 'var(--primary-accent-color)',
}

const Side = props => {
    const {
        type
    } = props
    const dispatch = useDispatch()
    let history = useHistory()
    const user = useSelector(state => state.auth.user)
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const body = useSelector(state => state.content.body)
    const activePageID = useSelector(state => state.auth.activePageID)
    const [showActive, setActive] = React.useState(true)
    const [showArchived, setArchived] = React.useState(false)

    const onSelectPage = (pageID, type) =>  {
        const oldUser = {...user}
        let updatedUser = {...user}
        updatedUser['activePageID'] = pageID
        updatedUser['activePageType'] = type
        dispatch(actionCreators.updateUser(updatedUser, oldUser))
        history.push(pageID.toLowerCase())
        window.scrollTo(0,0)
    }

    const onDrop = (values) => {
        const activePages = values['items']['PLACEHOLDER']
        console.log('[Side] onDrop', {current: user['activePages'], updated: activePages})

        if(!_.isEqual(activePages, user['activePages'])){
            let updatedUser = {...user}
            updatedUser['activePages'] = activePages
            dispatch(actionCreators.updateUser(updatedUser, user))
        }
    }

    const getBlocks = () => {
        let blocks = [
            // Modules
            <ListTile text='Home' key='home' icon={<FaHome style={{color: 'var(--text-color)'}}/>} 
                styles={activePageID === 'HOME' ? selectedStyle : defaultStyle} 
                onSelect={() => onSelectPage('HOME', pageContext.TYPES.MODULES)}/>,

            <ListTile text='Integrations' key='integration' icon={<FaBolt style={{color: 'var(--text-color)'}}/>} 
                styles={activePageID === 'INTEGRATIONS' ? selectedStyle : defaultStyle} 
                onSelect={() => onSelectPage('INTEGRATIONS', pageContext.TYPES.MODULES)}/>,  

            <ListTile text='Settings' key='settings' icon={<FaCog style={{color: 'var(--text-color)'}}/>} 
                styles={activePageID.includes('SETTINGS') ? selectedStyle : defaultStyle} 
                onSelect={() => onSelectPage('SETTINGS', pageContext.TYPES.MODULES)}/>,

            process.env.REACT_APP_ENV === 'DEVELOPMENT' && 
            <ListTile text='Sandbox' key='sandbox' icon={<FaCube style={{color: 'var(--text-color)'}}/>} 
                styles={activePageID === 'SANDBOX' ? selectedStyle : defaultStyle} 
                onSelect={() => onSelectPage('SANDBOX', pageContext.TYPES.MODULES)}/>,
        ]
        
        // Active pages
        blocks.push(
            <SideSection key='aactive' text='ACTIVE PAGES' 
                button={true} open={showActive} 
                contextMenu={<PageInput/>}
                onClick={() => setActive(!showActive)}/>
        )
        const activePages = user['activePages'].map(pageID => data['pages'][pageID])
        const treeItems = activePages.map(page => ({
            id: page['pageID'],
            parentId: null,
            block: <PageBlock page={page} style={{backgroundColor: activePageID === page['pageID'] ? 'var(--primary-accent-color)' : 'var(--secondary-color)'}} 
                    onSelect={() => onSelectPage(page['pageID'], pageContext.TYPES.PAGES)}/>,
            depth: 0,
            parents: [],
            containerId: 'PLACEHOLDER',
        }))
        const activeBlocks = <SortableForest onDrop={onDrop} id={activePageID} key={`${activePageID}_FOREST`}
                                trees={[{ id: 'PLACEHOLDER', tree: treeItems, disableDrag: true}]}
                                direction='VERTICAL' disableNesting disableVirtualWindowScroll disableVirtual
                                styles={{ treeWidth: 'auto', margin: 0, padding: 0 }}/>

        blocks.push(
            <AnimatePresence key='activePages' initial={false}>
                {showActive && 
                <PageContainer>
                    <Animation animation='expand'>
                        <PageContainer>{activePages.length > 0 ? activeBlocks : <Placeholder>No active pages</Placeholder>}</PageContainer>
                    </Animation>
                </PageContainer>}
            </AnimatePresence>
        )

        // Archived pages
        blocks.push(
            <SideSection key='archived' text='ARCHIVED PAGES' 
                button={false} open={showArchived} 
                onClick={() => setArchived(!showArchived)}/>
        )
        const archivedPages = Object.values(data['pages']).filter(page => page['status'] === pageContext.STATUS.ARCHIVE) 
        const archivedBlocks = archivedPages.map(page => (
            <PageBlock page={page} style={{backgroundColor: activePageID === page['pageID'] ? 'var(--primary-accent-color)' : 'var(--secondary-color)'}} 
                onSelect={() => onSelectPage(page['pageID'], pageContext.TYPES.PAGES)}/>
        ))
        blocks.push(
            <AnimatePresence key='archivedPages' initial={false}>
                {showArchived && 
                <PageContainer>
                    <Animation animation='expand'>
                        <PageContainer>{archivedPages.length > 0 ? archivedBlocks : <Placeholder>No archived pages</Placeholder>}</PageContainer>
                    </Animation>
                </PageContainer>}
            </AnimatePresence>
        )
        

        return blocks
    }

    switch(type){
        case 'Opened':
            return (
                <Wrapper>
                    <Container height={body ? body.scrollHeight : null}>
                        <ProfileSection img={user['picture']} name={user['first_name']} email={user['email']}
                            onSelect={() => onSelectPage('SETTINGS', pageContext.TYPES.MODULES)}/>
                        <PageBlocks padding='0'>{getBlocks()}</PageBlocks>
                        <UpgradeSection/>
                    </Container>
                </Wrapper>
            )
        case 'PopOut':
            return (
                <PopContainer>
                    <ProfileSection img={user['picture']} name={user['first_name']} email={user['email']}
                        onSelect={() => onSelectPage('SETTINGS', pageContext.TYPES.MODULES)}/>
                    <PageBlocks padding='0.5rem 1rem 1rem 1rem'>{getBlocks()}</PageBlocks>       
                    <UpgradeSection/>
                </PopContainer>
            )
    }
}

export default Side